import { call, put, select, takeLatest } from 'redux-saga/effects';
import { selectors as applicationSelectors } from '../../../../services/applications/reducers';
import { actions, initialState, selectors as logsSelectors, types } from './reducers';
import api from '../../../../services/api/api';
import formatRequestLogs from './utils/formatRequestLogs';
import formatFilterValues from './utils/formatFilterValues';
import { errors } from '../../../../localization/Application/Logs/logs';

export function* fetchAllCompatibleVehicles() {
  try {
    const filterValues = yield select(logsSelectors.getFilterValues);
    const applicationId = yield select(applicationSelectors.getSelectedApplication);

    const { data } = yield call(
      api.fetchAllCompatibleVehicles,
      applicationId,
    );

    const compatibleMakes = {};

    // sets all compatible makes to true
    Object.values(data).forEach((vehicle) => {
      compatibleMakes[vehicle.make] = true;
    });

    const updatedMakes = {};

    if (
      Object.values(filterValues.makes).length === 0
    ) {
      Object.values(data).forEach((vehicle) => {
        updatedMakes[vehicle.make] = true;
      });
    }

    yield put(actions.fetchAllCompatibleVehiclesSuccess(compatibleMakes));
    yield put(actions.updateFilterValues({
      ...filterValues,
      makes: Object.values(filterValues.makes).length !== 0
        ? filterValues.makes : updatedMakes,
      statusCodes: filterValues.statusCodes,
    }));
  } catch (error) {
    yield put(actions.fetchAllCompatibleVehiclesFailure(error));
  }
}

export function* fetchRequestLogs(action) {
  try {
    const { filterValues, newPaginationModel } = action.payload;

    const rowCount = yield select(logsSelectors.getRowCount);
    const applicationId = yield select(applicationSelectors.getSelectedApplication);

    const { data } = yield call(
      api.fetchRequestLogs,
      applicationId,
      {
        ...filterValues,
        mode: 'live',
      },
    );

    const updatedData = formatRequestLogs(data, filterValues.offset);

    const results = {
      pageInfo: {
        rows: updatedData.rows,
        columns: updatedData.cols,
        // Always keep rowCount from first fetch for offset pagination to work
        rowCount: newPaginationModel.page === 0 ? updatedData.totalCount : rowCount,
      },
    };

    yield put(actions.fetchRequestLogsSuccess(results));
    yield put(actions.updatePaginationModel(newPaginationModel));
  } catch (error) {
    yield put(actions.fetchRequestLogsFailure(error));
  }
}

export function* fetchExportData() {
  try {
    const filterValues = yield select(logsSelectors.getFilterValues);
    const applicationId = yield select(applicationSelectors.getSelectedApplication);

    const updatedFilterValues = formatFilterValues(filterValues);

    const { data } = yield call(
      api.fetchRequestsLogCsv,
      applicationId,
      // `limit` in the filterValues has to be undefined when fetching
      //  export data or you'll be capped at whatever it's set to
      {
        ...updatedFilterValues,
        limit: undefined,
        mode: 'live',
      },
    );

    yield put(actions.fetchExportDataSuccess(data));
  } catch (error) {
    yield put(actions.fetchExportDataFailure(error));
  }
}

export function* submitLogExportRequest(action) {
  const applicationId = yield select(applicationSelectors.getSelectedApplication);
  try {
    yield call(
      api.submitLogExportRequest,
      applicationId,
      action.payload,
    );
    yield put(actions.logExportRequestSuccess());
  } catch (error) {
    yield put(actions.logExportRequestFailure(error));
  }
}

export function* fetchLogDrawerVehicleDetails(action) {
  const { payload: vehicleId } = action;
  const applicationId = yield select(applicationSelectors.getSelectedApplication);
  // currently only available to 'live' vehicles, server will default to live mode
  const filterValues = { vehicle_id: vehicleId };

  try {
    const { data } = yield call(
      api.fetchConnectedVehicles,
      applicationId,
      filterValues,
    );

    const vehicleInfo = data.rows.find(row => row.vehicleId === vehicleId);

    if (!vehicleInfo) {
      throw new Error(errors.vehicleNotFound);
    }

    yield put(actions.fetchLogDrawerVehicleDetailsSuccess(vehicleInfo));
  } catch (error) {
    yield put(actions.fetchLogDrawerVehicleDetailsFailure(error));
  }
}


export function* fetchWebhookEvents(action) {
  try {
    const {
      payload: { filterValues, newPaginationModel },
    } = action;
    const applicationId = yield select(applicationSelectors.getSelectedApplication);

    const { data } = yield call(
      api.fetchWebhookEvents,
      applicationId,
      filterValues,
    );
    const results = {
      rows: data.rows,
      columns: data.cols,
      rowCount: data.paging.count,
    };

    const pageZero = newPaginationModel.page === 0;
    // Reset cursorMap whenever we fetch page 0
    if (pageZero) {
      yield put(
        actions.updateWebhookEventsCursorMap(initialState.webhookEvents.pageToNextCursorMap),
      );
    }

    yield put(actions.fetchWebhookEventsSuccess(results));

    const cursorMap = yield select(logsSelectors.getWebhookEventCursorMap);
    yield put(actions.updateWebhookEventsCursorMap({
      ...cursorMap,
      [newPaginationModel.page]: data.paging.cursor,
    }));
    yield put(actions.updateWebhookEventsPaginationModel(newPaginationModel));
  } catch (error) {
    yield put(actions.fetchWebhookEventsFailure(error));
  }
}

export default function* rootSaga() {
  yield takeLatest(types.FETCH_ALL_COMPATIBLE_VEHICLES_REQUEST, fetchAllCompatibleVehicles);
  yield takeLatest(types.FETCH_REQUEST_LOGS, fetchRequestLogs);
  yield takeLatest(types.FETCH_EXPORT_DATA, fetchExportData);
  yield takeLatest(types.SUBMIT_LOG_EXPORT_REQUEST, submitLogExportRequest);
  yield takeLatest(types.FETCH_LOG_DRAWER_VEHICLE_DETAILS_REQUEST, fetchLogDrawerVehicleDetails);
  yield takeLatest(types.FETCH_WEBHOOK_EVENTS_REQUEST, fetchWebhookEvents);
}
